/** A react-dropzone area + an explicit "browse" button. Batch (multiple) for the
 *  gallery's add-images flow; single (`multiple={false}`) for the detail drawer's
 *  replace flow. The parent decides what to do with the accepted `File[]`. */
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, Typography } from '@mui/material';
import { useCallback } from 'react';
import { useDropzone, type Accept } from 'react-dropzone';

interface Props {
  onFiles: (files: File[]) => void;
  multiple?: boolean;
  disabled?: boolean;
  /** Small variant for the detail drawer's replace control. */
  compact?: boolean;
  label?: string;
  /** react-dropzone accept map (MIME -> extensions). Defaults to images. */
  accept?: Accept;
  /** Override the browse button text (defaults to the image-flow wording). */
  browseLabel?: string;
}

const IMAGE_ACCEPT: Accept = { 'image/*': [] };

export function UploadDropzone({
  onFiles,
  multiple = true,
  disabled = false,
  compact = false,
  label,
  accept = IMAGE_ACCEPT,
  browseLabel,
}: Props) {
  const onDrop = useCallback(
    (accepted: File[]) => {
      if (accepted.length > 0) onFiles(accepted);
    },
    [onFiles],
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple,
    disabled,
    noClick: true,
    noKeyboard: true,
  });

  const heading = label ?? (multiple ? 'Drag images here' : 'Drag a replacement image here');
  const buttonText = browseLabel ?? (multiple ? 'Add images' : 'Choose file');

  return (
    <Box
      {...getRootProps()}
      sx={{
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : 'divider',
        borderRadius: 1,
        bgcolor: isDragActive ? 'action.hover' : 'transparent',
        p: compact ? 1.5 : 3,
        textAlign: 'center',
        transition: 'border-color 120ms, background-color 120ms',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {/* react-dropzone owns this input; the accessible name comes from the button below. */}
      <input {...getInputProps()} aria-label={buttonText} />
      {!compact && <CloudUploadIcon color="action" sx={{ fontSize: 32, mb: 0.5 }} />}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {heading}
      </Typography>
      <Button
        variant="outlined"
        size={compact ? 'small' : 'medium'}
        startIcon={<CloudUploadIcon />}
        onClick={open}
        disabled={disabled}
      >
        {buttonText}
      </Button>
    </Box>
  );
}
